import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';

import { css, keyframes } from '@sorare/css';

import { Star } from '#src/atoms/icons/Star.tsx';
import { range } from '#src/lib/arrays.ts';

export const STAR_FLIGHT_TRANSITION_TYPE = 'gameplay-tier-star-flight';

const starFlightTransitionName = (order: number) =>
  `gameplay-tier-star-flight-${order}`;

const arc = keyframes`
  45% {
    translate: 0 calc(-8 * var(--unit));
    scale: 1.6;
    rotate: 90deg;
  }
  90% {
    opacity: 1;
  }
  to {
    scale: 0.8;
    rotate: 180deg;
    opacity: 0;
  }
`;

const fadeOut = keyframes`
  to {
    opacity: 0;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
`;

const spentSlot = css`
  view-transition-class: gameplay-tier-star-slot;
`;

const flyingStar = css`
  view-transition-class: gameplay-tier-star;
  pointer-events: none;

  @at-root :root:active-view-transition-type(gameplay-tier-star-flight) {
    &::view-transition-old(root),
    &::view-transition-new(root) {
      animation: none;
    }

    &::view-transition-group(compose-picked-card) {
      animation-duration: 700ms;
      animation-timing-function: cubic-bezier(0.6, 0, 0.3, 1);
    }

    &::view-transition-old(compose-picked-card) {
      display: none;
    }

    &::view-transition-new(compose-picked-card) {
      animation: none;
    }

    &::view-transition-group(.gameplay-tier-star) {
      z-index: 1;
      animation-duration: 700ms;
      animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
    }

    &::view-transition-image-pair(.gameplay-tier-star) {
      animation: 700ms ease-in-out both ${arc};
      filter: drop-shadow(0 0 6px rgba(var(--c-rgb-yellow-400), 0.8));
    }

    &::view-transition-old(.gameplay-tier-star),
    &::view-transition-new(.gameplay-tier-star) {
      animation: none;
    }

    &::view-transition-old(.gameplay-tier-star-slot) {
      animation: 150ms ease-in 650ms both ${fadeOut};
    }

    &::view-transition-new(.gameplay-tier-star-slot) {
      animation: 150ms ease-out 650ms both ${fadeIn};
    }

    &::view-transition-group(gameplay-tier-star-flight-1),
    &::view-transition-image-pair(gameplay-tier-star-flight-1) {
      animation-delay: 80ms;
    }

    &::view-transition-old(gameplay-tier-star-slot-1-0),
    &::view-transition-old(gameplay-tier-star-slot-1-1),
    &::view-transition-new(gameplay-tier-star-slot-1-0),
    &::view-transition-new(gameplay-tier-star-slot-1-1) {
      animation-delay: 730ms;
    }

    &::view-transition-group(gameplay-tier-star-flight-2),
    &::view-transition-image-pair(gameplay-tier-star-flight-2) {
      animation-delay: 160ms;
    }

    &::view-transition-old(gameplay-tier-star-slot-2-0),
    &::view-transition-old(gameplay-tier-star-slot-2-1),
    &::view-transition-new(gameplay-tier-star-slot-2-0),
    &::view-transition-new(gameplay-tier-star-slot-2-1) {
      animation-delay: 810ms;
    }

    &::view-transition-group(gameplay-tier-star-flight-3),
    &::view-transition-image-pair(gameplay-tier-star-flight-3) {
      animation-delay: 240ms;
    }

    &::view-transition-old(gameplay-tier-star-slot-3-0),
    &::view-transition-old(gameplay-tier-star-slot-3-1),
    &::view-transition-new(gameplay-tier-star-slot-3-0),
    &::view-transition-new(gameplay-tier-star-slot-3-1) {
      animation-delay: 890ms;
    }

    &::view-transition-group(gameplay-tier-star-flight-4),
    &::view-transition-image-pair(gameplay-tier-star-flight-4) {
      animation-delay: 320ms;
    }

    &::view-transition-old(gameplay-tier-star-slot-4-0),
    &::view-transition-old(gameplay-tier-star-slot-4-1),
    &::view-transition-new(gameplay-tier-star-slot-4-0),
    &::view-transition-new(gameplay-tier-star-slot-4-1) {
      animation-delay: 970ms;
    }
  }
`;

const source = css`
  position: relative;
  display: block;
`;

const sourceStar = css`
  position: absolute;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  width: var(--double-unit);
  height: var(--double-unit);
  color: var(--c-yellow-400);

  &.half {
    clip-path: inset(0 50% 0 0);
  }
`;

const landingStar = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  aspect-ratio: 1;
  opacity: 0;

  &.betweenSlots {
    left: 50%;
  }
`;

const flightStyle = (order: number) =>
  ({ viewTransitionName: starFlightTransitionName(order) }) as CSSProperties;

export type StarFlight = { stars: number; landed: boolean };

export const spentStarSlotProps = (transitionName: string) => ({
  className: spentSlot,
  style: { viewTransitionName: transitionName },
});

type SourceProps = {
  stars: number;
  children: ReactNode;
};

export const GameplayTierStarFlightSource = ({
  stars,
  children,
}: SourceProps) => (
  <span className={source}>
    {children}
    {range(Math.ceil(stars)).map(order => (
      <Star
        key={order}
        className={classNames(flyingStar, sourceStar, {
          half: order + 1 > stars,
        })}
        style={flightStyle(order)}
        aria-hidden
      />
    ))}
  </span>
);

type Landing = { order: number; index: number; betweenSlots: boolean };

export const starFlightPlan = (
  remainingBefore: number,
  stars: number,
  ceiling: number
) => {
  const clamp = (value: number) => Math.max(0, Math.min(value, ceiling));
  const remainingAfter = remainingBefore - stars;

  const flights = range(Math.ceil(stars))
    .map(order => ({
      order,
      high: clamp(remainingBefore - order),
      low: clamp(Math.max(remainingAfter, remainingBefore - order - 1)),
    }))
    .filter(({ low, high }) => high > low);

  const landings: Landing[] = flights.map(({ order, low, high }) => {
    const middle = (low + high) / 2;
    const betweenSlots = Number.isInteger(middle);
    return {
      order,
      index: betweenSlots
        ? middle - 1
        : Math.min(ceiling - 1, Math.floor(middle)),
      betweenSlots,
    };
  });

  const slotTransitionNames = new Map<number, string>();
  flights.forEach(({ order, low, high }) => {
    range(ceiling)
      .filter(index => index < high && index + 1 > low)
      .forEach((index, rank) =>
        slotTransitionNames.set(
          index,
          `gameplay-tier-star-slot-${order}-${rank}`
        )
      );
  });

  return { landings, slotTransitionNames };
};

export const GameplayTierStarLanding = ({
  order,
  betweenSlots,
}: Omit<Landing, 'index'>) => (
  <span
    className={classNames(flyingStar, landingStar, { betweenSlots })}
    style={flightStyle(order)}
  />
);
